import { Category } from "app/components/category/category.component";
import { CountrySelector } from "app/components/country-selector/country-selector.component";
import Loader from "app/components/icons/Loader";
import clsx from "clsx";
import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from "next";
import { useRouter } from "next/dist/client/router";
import React, { useRef, useState } from "react";
import { Category as CategoryType } from "services-hooks/types";
import styles from "../index.module.scss";
import { useTranslate } from "app/hooks/lang";
import { useGoBackHistory } from "app/hooks/go-back.hook";
import { userActions } from "app/store/slices/user";
import { useDispatch } from "react-redux";
import Link from "next/link";
import { getPillowCategoryPath } from "services-hooks/services";
import { getCircularReplacer } from "app/utils/array";
import { Country } from "app/context/country.context";
import { NextSeo } from "next-seo";
import { getServersideLocale } from "app/utils/getServersideLocale";

export const getStaticPaths: GetStaticPaths = (context) => {
  const paths = [
    { params: { country: "es" }, locale: "es" },
    { params: { country: "be" }, locale: "fr" },
    { params: { country: "fr" }, locale: "fr" },
    { params: { country: "de" }, locale: "de" },
    { params: { country: "gb" }, locale: "en" },
    { params: { country: "pl" }, locale: "pl" },
    { params: { country: "us" }, locale: "en" },
  ];
  return {
    paths,
    fallback: true,
  };
};

export const getStaticProps: GetStaticProps<{
  countryData: Country;
  initialData: {
    podcastCategory: CategoryType;
    podcasts: CategoryType[];
  }[];
}> = async (context) => {
  try {
    const countryCode = context.params
      ? (context.params["country"] as string)
      : null;
    if (!countryCode) {
      throw new Error("Missing countryCode");
    }
    const countryData = await getPillowCategoryPath(
      `categories/radioline_countries/${countryCode}`,
      {},
      {
        headers: {
          authorization: `Bearer ${process.env.SERVER_API_KEY}`,
          "X-PillowLocale": getServersideLocale(context.locale),
        },
      }
    );
    let countryObject: Country | null = null;
    const pageData = countryData.data.path;
    if (pageData) {
      const page = pageData[pageData.length - 1];
      if (page.name && page.logo && page.permalink) {
        countryObject = {
          countryCode: countryCode as string,
          name: page.name,
          logo: page.logo,
          permalink: page.permalink,
          preset: false,
        };
        const podcastsData = await getPillowCategoryPath(
          `categories/radioline_countries/${countryCode}/discover_v2/podcasts`,
          {
            pageSize: 90,
          },
          {
            headers: {
              authorization: `Bearer ${process.env.SERVER_API_KEY}`,
              "X-PillowLocale": getServersideLocale(context.locale),
            },
          }
        );
        const initialData: {
          podcastCategory: CategoryType;
          podcasts: CategoryType[];
        }[] = await Promise.all(
          podcastsData.data?.body?.content?.map(async (podcastCategory) => ({
            podcastCategory: podcastCategory,
            podcasts:
              (
                await getPillowCategoryPath(
                  podcastCategory.permalink!,
                  {
                    pageSize: 100,
                  },
                  {
                    headers: {
                      authorization: `Bearer ${process.env.SERVER_API_KEY}`,
                      "X-PillowLocale": getServersideLocale(context.locale),
                    },
                  }
                )
              ).data.body?.content?.map(({ monitoring, ...content }) => ({
                ...content,
                tags: [],
              })) || [],
          })) || []
        );
        return {
          props: {
            countryData: countryObject,
            initialData: JSON.parse(
              JSON.stringify(initialData, getCircularReplacer())
            ),
          },
          revalidate: 43200,
        };
      }
    }
    return {
      notFound: true,
    };
  } catch (e) {
    console.error(e);
    return {
      notFound: true,
    };
  }
};

export default function DiscoverPodcasts({
  initialData,
  countryData,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const t = useTranslate();
  const router = useRouter();
  return (
    <div className={styles.container}>
      {router.isFallback ? (
        <div className="loader">
          <Loader />
        </div>
      ) : (
        <>
          <NextSeo
            titleTemplate={`%s`}
            title={t("seo_title_discoverPodcast", {
              var1: countryData.name,
            })}
            description={t("seo_description_discoverPodcast", {
              var1: countryData.name,
            })}
            openGraph={{
              images: [
                {
                  url: "https://www.radioline.co/open-graph.png",
                  alt: "Radioline",
                },
              ],
            }}
          />
          <PodcastDiscovery
            key={countryData.permalink}
            initialData={initialData}
            countryData={countryData}
          />
        </>
      )}
    </div>
  );
}
interface PodcastDiscoveryProps {
  countryData: Country;
  initialData: {
    podcastCategory: CategoryType;
    podcasts: CategoryType[];
  }[];
}

const PodcastDiscovery: React.FC<PodcastDiscoveryProps> = ({
  countryData,
  initialData,
}) => {
  const router = useRouter();
  const t = useTranslate();
  useGoBackHistory("DISCOVER/COUNTRY");
  const [countrySelectorOpen, setCountrySelectorOpen] = useState(false);
  const countryVariables = {
    "--image-background": `url("${countryData?.logo}")`,
  } as React.CSSProperties;
  const loadMoreTriggerRef = useRef(null);
  const dispatch = useDispatch();
  return (
    <>
      <div className={clsx(styles.header)} data-navigate-row>
        <div className={styles.tabs_wrapper}>
          <div className={styles.tabs}>
            <Link href={`/discover/radio/${countryData.countryCode}`} passHref>
              <a className={styles.tab} data-navigate-item>
                {t("tabs.stations")}
              </a>
            </Link>
            <Link
              href={`/discover/podcast/${countryData.countryCode}`}
              passHref
            >
              <a className={clsx(styles.tab, styles.active)} data-navigate-item>
                {t("tabs.podcasts")}
              </a>
            </Link>
          </div>
        </div>
        <div
          className={styles.country_wrapper}
          onClick={() => setCountrySelectorOpen(true)}
          data-navigate-item
          tabIndex={0}
        >
          <p className={styles.country_name}>{t("country-selector")}</p>
          <div className={styles.country_logo} style={countryVariables} />
        </div>
      </div>
      <div className={styles.podcasts_tab}>
        {initialData.map((category, index) => (
          <Category
            key={`discover-podcasts-category-${category.podcastCategory.permalink}`}
            podcast
            items={category.podcasts}
            category={category.podcastCategory}
            className={styles.category_section}
          />
        ))}
        <div ref={loadMoreTriggerRef} />
      </div>
      {countrySelectorOpen && (
        <CountrySelector
          onClose={() => setCountrySelectorOpen(false)}
          onSelect={(category) => {
            dispatch(
              userActions.setSelectedDiscoverLanguage({
                logo: category.logo!,
                name: category.name!,
                permalink: category.permalink!,
              })
            );
            setCountrySelectorOpen(false);
            if (category.permalink) {
              router.push(
                `/discover/podcast/${category.permalink.split("/").pop()}`
              );
            } else {
              console.error("WRONG FORMAT");
            }
          }}
        />
      )}
    </>
  );
};
